import { FieldRenderProps } from "react-final-form";
import { Filter } from "../components/functional/OrderFailureCodes/types";
import { Role, RoleLabelMap, Roles } from "../context/userinfo/types";
import { FilterTruthyValues } from "./types";
import { SetURLSearchParams } from "react-router-dom";
import { OrderConfigurationsResponse, ProbeConfigResponse } from "@fintechprimitives/fpapi";
import { ProbeService } from "../accessControl/RenderByProbeServiceConfig";
type CronType = "minute" | "hour" | "dayOfMonth" | "month" | "dayOfWeek";

type TenantEnv = "qa" | "staging" | "sandbox" | "production";

type FilterTruthyValuesOptions = {
  ignoreZero?: boolean;
  ignoreFalse?: boolean;
};

const TENANT_ENV: TenantEnv = import.meta.env.VITE_APP_TENANT_ENV as TenantEnv;

export const tenantEnv = {
  isQa: TENANT_ENV === "qa",
  isStaging: TENANT_ENV === "staging",
  isSandbox: TENANT_ENV === "sandbox",
  isProduction: TENANT_ENV === "production",
  isQaOrStaging: ["qa", "staging"].includes(TENANT_ENV),
  isStagingOrSandbox: ["staging", "sandbox"].includes(TENANT_ENV),
  getEnvironmentName: () => {
    return TENANT_ENV;
  },
};

export const isEmptyObject = (obj: any) => {
  if (typeof obj === "object" && !Array.isArray(obj) && obj !== null) {
    return Object.keys(obj).length === 0;
  } else {
    return true;
  }
};

export const isEmptyArray = (arr: any[]) => {
  if (Array.isArray(arr) && arr.length) {
    return false;
  } else {
    return true;
  }
};

export function toTitleCase(str: string | undefined = undefined) {
  if (!str) {
    return undefined;
  }

  const spacedStr = str.split(" ");
  if (spacedStr.length) {
    const spacesTrim = spacedStr.filter((word) => word);
    const result = spacesTrim
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
    return result;
  }
  return str.charAt(0).toUpperCase() + str.substring(1).toLowerCase();
}

/**
 * @method toISOStringWithTZ - Convert input date object into ISO8601 string with time zone
 * @param date DateObject
 * USECASE: Factor in TimeZone while converting date object to ISO8601 string
 */
const toISOStringWithTZ = (date: Date) => {
  const tzo = -date.getTimezoneOffset();
  const dif = tzo >= 0 ? "+" : "-";
  const pad = (num: number) => {
    return (num < 10 ? "0" : "") + num;
  };

  return (
    date.getFullYear() +
    "-" +
    pad(date.getMonth() + 1) +
    "-" +
    pad(date.getDate()) +
    "T" +
    pad(date.getHours()) +
    ":" +
    pad(date.getMinutes()) +
    ":" +
    pad(date.getSeconds()) +
    dif +
    pad(Math.floor(Math.abs(tzo) / 60)) +
    ":" +
    pad(Math.abs(tzo) % 60)
  );
};

export const formatDate = (d: Date, format: string) => {
  switch (format) {
    case "yyyy-mm-dd": // 2022-10-28
      if (Object.prototype.toString.call(d) === "[object Date]") {
        return toISOStringWithTZ(d).split("T")[0];
      } else {
        return toISOStringWithTZ(new Date()).split("T")[0];
      }
    case "dd-MMM-yyyy": // 28-Nov-2022
      if (Object.prototype.toString.call(d) === "[object Date]") {
        const year = new Intl.DateTimeFormat("en", { year: "numeric" }).format(d);
        const month = new Intl.DateTimeFormat("en", { month: "short" }).format(d);
        const day = new Intl.DateTimeFormat("en", { day: "2-digit" }).format(d);
        return `${day}-${month}-${year}`;
      } else {
        const date = new Date();
        const year = new Intl.DateTimeFormat("en", { year: "numeric" }).format(date);
        const month = new Intl.DateTimeFormat("en", { month: "short" }).format(date);
        const day = new Intl.DateTimeFormat("en", { day: "2-digit" }).format(date);
        return `${day}-${month}-${year}`;
      }
    case "dd-MM-yyyy":
      if (!d) {
        return undefined;
      }

      // Always zero-pad day/month (e.g. 22-07-2026). Locale-only formatting can
      // emit unpadded values like 22-7-2026, which APIs parsing dd-MM-yyyy reject.
      {
        const dateObj = Object.prototype.toString.call(d) === "[object Date]" ? d : new Date(d);

        return dateObj
          .toLocaleDateString("en-IN", {
            timeZone: "Asia/Kolkata",
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })
          .replaceAll("/", "-");
      }
    default:
      if (Object.prototype.toString.call(d) === "[object Date]") {
        return toISOStringWithTZ(d).split("T")[0];
      } else {
        return toISOStringWithTZ(new Date()).split("T")[0];
      }
  }
};

export const scrollToErrorField = (errorFieldName: string) => {
  if (errorFieldName) {
    const inputField = document.querySelector(`[data-name=${errorFieldName}]`);
    if (inputField) {
      inputField.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center",
      });
    }
  }
  return;
};

export const convert_dd_mm_yyyy_to_mm_dd_yyyy = (dateStr: string) => {
  const splitDateStr = dateStr?.split("-");

  if (splitDateStr?.length !== 3) {
    // current date str
    return new Date().toLocaleDateString("en").replaceAll("/", "-");
  }

  const [day, month, year] = splitDateStr;

  return `${month}-${day}-${year}`;
};

export const formatDateObjToMmDdYyyy = (date: Date) => {
  return date
    .toLocaleDateString("en-in", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
    .replaceAll("/", "-");
};

export const formatDateObjToYyyyMmDd = (date: Date) => {
  return date
    .toLocaleDateString("en-in", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
    .split("/")
    .reverse()
    .join("-");
};

export const getLastMonthDateRange = (format: string = "MM-DD-YYYY") => {
  const currentDate = new Date();
  const lastMonth = currentDate.getMonth() - 1;
  const currentYear = currentDate.getFullYear();

  const startDate = new Date(currentYear, lastMonth, 1);

  const endDate = new Date(currentYear, lastMonth + 1, 0); // 0 sets the day to day before 1st of the given month

  if (format === "YYYY-MM-DD") {
    return {
      startDate: formatDateObjToYyyyMmDd(startDate),
      endDate: formatDateObjToYyyyMmDd(endDate),
    };
  }

  return {
    startDate: formatDateObjToMmDdYyyy(startDate),
    endDate: formatDateObjToMmDdYyyy(endDate),
  };
};

export const flattenBatchFiltersArrayForPills = (
  label: string | undefined,
  filters: Filter[] | undefined,
  fieldValuesDisplayLabel?: string | undefined
) => {
  if (
    filters &&
    filters?.length > 1 &&
    (label === "Created at" || label === "Generated date and time")
  ) {
    //This means it's a date range
    return [{ [label ?? "Label"]: fieldValuesDisplayLabel }];
  }

  const filterArray = filters?.map((filter: Filter) => {
    const { value } = filter;

    if (Array.isArray(value)) {
      return value.flatMap((val) => ({ [label ?? "Label"]: val }));
    } else {
      return [{ [label ?? "Label"]: value }];
    }
  });

  return filterArray?.flat();
};

export const activeTenantLocalStorage = {
  getActiveTenant() {
    return localStorage.getItem("activeTenant");
  },
  setActiveTenant(tenant: string) {
    return localStorage.setItem("activeTenant", tenant);
  },
  removeActiveTenant() {
    return localStorage.removeItem("activeTenant");
  },
};

export const getOptionsIndex = (
  data: any[],
  value?: string | number | null | boolean,
  options?: { isNotArrayOfObjects?: boolean; customProperty?: string }
) => {
  const isNotArrayOfObjects = options?.isNotArrayOfObjects;
  const customProperty = options?.customProperty || "value"; // by default searches for "value" in given array of objects. If we need to look for a custom key pass this optional argument

  return data.findIndex((ele: any) => {
    if (isNotArrayOfObjects) {
      return ele === value;
    } else {
      // Traversal inside a deeply nested object
      if (customProperty.includes(".")) {
        const nestedAccessArray = customProperty.split(".");

        let nestedAccessValue;

        for (const property of nestedAccessArray) {
          if (!nestedAccessValue) {
            nestedAccessValue = ele[property];
          } else {
            nestedAccessValue = nestedAccessValue[property];
          }
        }

        return nestedAccessValue === value;
      }

      return ele[customProperty] === value;
    }
  });
};

export const parseJWT = (token: string) => {
  const base64Url = token.split(".")[1];
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const JSONPayload = decodeURIComponent(
    window
      .atob(base64)
      .split("")
      .map((c) => {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join("")
  );

  return JSON.parse(JSONPayload);
};

export const getFileNameFromContentDispositionHeader = (
  contentDispositionHeader: string | undefined
) => {
  if (!contentDispositionHeader) return undefined;

  const contentDispositionHeaderSplit = contentDispositionHeader?.split(";");
  const filenameHeaderValue = contentDispositionHeaderSplit
    ?.find((value) => value?.trim()?.startsWith("filename="))
    ?.trim();
  const filename = filenameHeaderValue?.split("=")?.[1]?.replaceAll('"', "");

  return filename;
};

export const removePrefixFromString = (
  fullString: string,
  prefixString: string,
  fallBackString?: string
) => {
  if (fullString && fullString?.startsWith(prefixString)) {
    return fullString?.slice(prefixString?.length);
  }

  return fallBackString ?? "/";
};

export const isAuthorisedRole = (allowedRoles: Roles, currentRoles: Roles | undefined) => {
  return allowedRoles.some((role) => currentRoles?.includes(role));
};

export const isOnlyOndcAnalyst = (roles: Roles | undefined) => {
  return roles?.length === 1 && roles[0] === Role.ONDC_ANALYST;
};

/**
 * Whether ONDC is a supported order gateway for the tenant (`/configurations/orders`).
 *
 * @example
 * isOndcOrderGatewaySupported({ ondc: true, rta: false, ncdex: false, bse: false }) // true
 * isOndcOrderGatewaySupported({ ondc: false, rta: true, ncdex: false, bse: false }) // false
 * isOndcOrderGatewaySupported(null) // false
 * isOndcOrderGatewaySupported(undefined) // false
 */
export const isOndcOrderGatewaySupported = (
  supportedGateways: OrderConfigurationsResponse["supported_gateways"] | null | undefined
): boolean => {
  return supportedGateways?.ondc === true;
};

export const isProbeServiceEnabled = (
  requiredProbeServices: ProbeService[],
  probeConfig: ProbeConfigResponse | null
) => {
  if (!probeConfig) {
    return false;
  }

  return requiredProbeServices.every((service) => probeConfig?.[service] === true);
};

export const formatDateForTable = (
  date: Date | null | undefined | string,
  format: string = "dd MMM yyyy HH:mm",
  skipFormattingIfMasked: boolean = true
): string => {
  if (!date) {
    return "-";
  }

  if (skipFormattingIfMasked && typeof date === "string" && isDateMasked(date)) {
    return date;
  }

  let options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
  };

  if (format.includes("HH:mm")) {
    options = { ...options, hour: "numeric", minute: "numeric", hour12: true };
  }

  if (format === "dd MMM yyyy HH:mm:ss" || format === "dd MMM yyyy HH:mm:ss:ms") {
    options.second = "numeric";
  }

  if (format === "dd MMM yyyy HH:mm:ss:ms") {
    options.fractionalSecondDigits = 2;
  }

  const formattedDate = new Date(date).toLocaleString("en-US", options);

  return formattedDate;
};

/**
 * Formats an "MM-YYYY" string as long month + year (e.g. "01-2026" → "January 2026").
 * Returns "-" when the value is missing. Returns the original string when it is not valid MM-YYYY.
 *
 * @example
 * formatMonthYear("01-2026") // "January 2026"
 * formatMonthYear("12-2025") // "December 2025"
 * formatMonthYear(null) // "-"
 * formatMonthYear("Q1 2026") // "Q1 2026"
 */
export const formatMonthYear = (value: string | null | undefined): string => {
  if (!value) {
    return "-";
  }

  const match = /^(\d{2})-(\d{4})$/.exec(value.trim());
  if (!match) {
    return value;
  }

  const month = Number(match[1]);
  const year = Number(match[2]);
  if (month < 1 || month > 12) {
    return value;
  }

  // Day 1 avoids timezone edge cases when constructing from month/year only
  return new Date(year, month - 1, 1).toLocaleString("en-US", {
    month: "long",
    year: "numeric",
  });
};

/**
 * Parses a date-like value to epoch ms for numeric sorting.
 * Returns null when missing or unparseable.
 *
 * @example
 * toSortableTimestamp("2026-10-29T07:00:00.000+0530") // 1761701400000 (approx)
 * @example
 * toSortableTimestamp(null) // null
 * @example
 * toSortableTimestamp("not-a-date") // null
 */
export const toSortableTimestamp = (value: unknown): number | null => {
  if (!value) {
    return null;
  }

  const timestamp = new Date(value as string | Date).getTime();

  return Number.isNaN(timestamp) ? null : timestamp;
};

export const sortByDateFieldDesc = <T, K extends keyof T>(
  items: readonly T[] | null | undefined,
  field: K
): T[] => {
  if (!items?.length) {
    return [];
  }

  return [...items].sort((a, b) => {
    const aTimestamp = toSortableTimestamp(a[field]);
    const bTimestamp = toSortableTimestamp(b[field]);

    if (aTimestamp === null && bTimestamp === null) {
      return 0;
    }

    if (aTimestamp === null) {
      return 1;
    }

    if (bTimestamp === null) {
      return -1;
    }

    return bTimestamp - aTimestamp;
  });
};

export const anyCharacterExcluding = (characters: any) => {
  return new RegExp(`[^${characters}]`, "g");
};

/** Strips spaces, dashes, plus, and parentheses from a phone-number search string. */
export const getPhoneDigitsOnly = (value: string) => value.replace(/[\s\-+()]/g, "");

/**
 * Checks whether a search string is effectively a phone-number input.
 * Strips common phone formatting (spaces, `-`, `+`, `()`) via `getPhoneDigitsOnly`,
 * then returns true only if what remains is non-empty and all digits.
 *
 * @example
 * isPhoneNumberSearch("9876543210") // true
 * isPhoneNumberSearch("+91 98765-43210") // true
 * isPhoneNumberSearch("user@email.com") // false
 * isPhoneNumberSearch("invp_abc") // false
 */
export const isPhoneNumberSearch = (value: string) => {
  const digitsOnly = getPhoneDigitsOnly(value);
  return digitsOnly.length > 0 && /^\d+$/.test(digitsOnly);
};

/**
 * Checks whether a string is an integer id (digits only, no formatting).
 *
 * @example
 * isIntegerId("12345") // true
 * isIntegerId("9876543210") // true
 * isIntegerId("+91 12345") // false
 */
export const isIntegerId = (value: string) => {
  return /^\d+$/.test(value);
};

/**
 * Checks whether a string is a UUID in the canonical 8-4-4-4-12 hex format.
 *
 * @example
 * isUuid("9e12c3d8-a3d6-49cc-8b24-9c1b939325bd") // true
 * isUuid("9e12c3d8a3d649cc8b249c1b939325bd") // false
 * isUuid("FAVPS3751B") // false
 */
export const isUuid = (value: string) => {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
};

export const isUpstreamError = (e: any) => {
  const axiosProperties = {
    data: 1,
    status: 1,
    statusText: 1,
    headers: 1,
    config: 1,
  };

  for (const key in axiosProperties) {
    if (!Object.prototype.hasOwnProperty.call(e, key)) {
      return false;
    }
  }

  return true;
};

export const maskPassword = (password?: string) => {
  if (!password) {
    return "-";
  }

  if (password?.length >= 1 && password?.length <= 6) {
    return "********";
  }

  return "****" + password?.slice(-4);
};

export const isDateMasked = (date: string | null | undefined) => {
  return date?.includes("*");
};

export const getLast30MinsDateRangeStr = () => {
  const date30MinsAgo = new Date();
  date30MinsAgo.setMinutes(date30MinsAgo.getMinutes() - 30);

  return `${Number(date30MinsAgo)}-${Number(new Date())}`;
};

export const getCurrentFinancialYearDate = () => {
  const startDate = new Date();
  startDate.setMonth(3);
  startDate.setDate(1);

  const endDate = new Date();

  return `${formatDateForTable(startDate, "dd MMM yyyy")} - ${formatDateForTable(
    endDate,
    "dd MMM yyyy"
  )}`;
};

export const getRealmSpecificAuthorityURL = (authorityUrl: string, realm: string) => {
  const currentAuthorityURL = authorityUrl.split("/");
  currentAuthorityURL?.pop();
  const tenantAuthorityURL = currentAuthorityURL?.join("/") + `/${realm}`;

  return tenantAuthorityURL;
};

export const mutateField = ([field, value]: any, state: any, { changeValue }: any) => {
  changeValue(state, field, () => value);
};

const capitalWords = [
  "Dhfl",
  "Lic",
  "Icici",
  "Sbi",
  "Boi",
  "Axa",
  "Elss",
  "Idfc",
  "Hdfc",
  "L&t",
  "Dsp",
  "Uti",
];

export const formatBankName = (bankName: string) => {
  const abbreviatedBankNames = [...capitalWords];

  if (bankName) {
    const abbreviation = abbreviatedBankNames.find((abbreviatedName) => {
      return bankName.toLowerCase().includes(abbreviatedName.toLowerCase());
    });

    if (abbreviation) {
      return toTitleCase(bankName.replace(abbreviation, abbreviation.toUpperCase()));
    } else {
      return toTitleCase(bankName);
    }
  }
  return "";
};

export const memoize = (fn: any) => {
  let lastArg: any;
  let lastResult: any;
  return (arg: any) => {
    if (arg !== lastArg) {
      lastArg = arg;
      lastResult = fn(arg);
    }
    return lastResult;
  };
};

export const hasVisitedConsoleSessionStorage = {
  get() {
    return sessionStorage.getItem("hasVisitedConsole");
  },
  set(value: string) {
    sessionStorage.setItem("hasVisitedConsole", value);
  },
  remove() {
    sessionStorage.removeItem("hasVisitedConsole");
  },
};

export const showFieldError = (props: FieldRenderProps<any, HTMLElement, any>) => {
  return props.meta.error && props.meta.touched ? "error" : undefined;
};

export const getStatus = (props: FieldRenderProps<any>) => {
  return showFieldError(props) ? "error" : undefined;
};

export interface ListResponse<T> {
  object: "list";
  data: T[];
}

export const getEitherValue = (value1: any, value2: string | null = "-") => {
  return value1 ? value1 : value2;
};

export const restrictEditInProduction = (defaultExpression: boolean) => {
  return defaultExpression && tenantEnv.isProduction;
};

const daysOfWeekMap: string[] = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
const monthsMap: string[] = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function handleAsterisk(type: CronType): string {
  switch (type) {
    case "minute":
      return "every minute";
    case "hour":
      return "every hour";
    case "dayOfMonth":
      return "every day of the month";
    case "month":
      return "every month";
    case "dayOfWeek":
      return "every day";
  }
}

function handleSlash(field: string, type: CronType): string {
  const [start, interval] = field.split("/");
  switch (type) {
    case "minute":
      return `every ${interval} minutes starting at minute ${start}`;
    case "hour":
      return `every ${interval} hours starting at hour ${start}`;
    case "dayOfMonth":
      return `every ${interval} days starting at day ${start} of the month`;
    case "month":
      return `every ${interval} months starting at month ${start}`;
    case "dayOfWeek":
      return `every ${interval} days starting at ${daysOfWeekMap[parseInt(start, 10)]}`;
  }
}

function handleDash(field: string, type: CronType): string {
  const [start, end] = field.split("-");
  switch (type) {
    case "minute":
      return `every minute between ${start} and ${end}`;
    case "hour":
      return `every hour between ${start} and ${end}`;
    case "dayOfMonth":
      return `every day between ${start} and ${end} of the month`;
    case "month":
      return `every month between ${monthsMap[parseInt(start, 10) - 1]} and ${
        monthsMap[parseInt(end, 10) - 1]
      }`;
    case "dayOfWeek":
      return `every day between ${daysOfWeekMap[parseInt(start, 10)]} and ${
        daysOfWeekMap[parseInt(end, 10)]
      }`;
  }
}

function handleDefault(field: string, type: CronType): string {
  switch (type) {
    case "minute":
      return `at minute ${field}`;
    case "hour":
      return `at hour ${field}`;
    case "dayOfMonth":
      return `on day ${field} of the month`;
    case "month":
      return `in ${monthsMap[parseInt(field, 10) - 1]}`;
    case "dayOfWeek":
      return `on ${daysOfWeekMap[parseInt(field, 10)]}`;
  }
}

function parseCronField(field: string, type: CronType): string {
  if (field === "*") {
    return handleAsterisk(type);
  }

  if (field.includes("/")) {
    return handleSlash(field, type);
  }

  if (field.includes("-")) {
    return handleDash(field, type);
  }

  return handleDefault(field, type);
}

// Example output convertCronToText("0 7 * * *") = "At 07:00, every day of the month, every month, every day"
export function convertCronToText(cronExpression?: string): string {
  if (!cronExpression) {
    return "-";
  }
  const [minute, hour, dayOfMonth, month, dayOfWeek] = cronExpression.split(" ");

  const humanReadable = {
    minute: parseCronField(minute, "minute"),
    hour: parseCronField(hour, "hour"),
    dayOfMonth: parseCronField(dayOfMonth, "dayOfMonth"),
    month: parseCronField(month, "month"),
    dayOfWeek: parseCronField(dayOfWeek, "dayOfWeek"),
  };

  return `At ${hour.padStart(2, "0")}:${minute.padStart(2, "0")}, ${humanReadable.dayOfMonth}, ${
    humanReadable.month
  }, ${humanReadable.dayOfWeek}`
    .replace(/\s+/g, " ")
    .trim();
}

export function isValidRole(role: string): role is Role {
  const validRoles = Object.entries(Role).map((val) => val[1].toString());
  return validRoles.includes(role);
}

export function fetchRoleLabel(role: string): string {
  if (isValidRole(role)) {
    return RoleLabelMap[role] ?? role;
  } else {
    return role;
  }
}

export const filterTruthyValues = <T extends Record<string, any>>(
  obj: T,
  { ignoreZero = false, ignoreFalse = false }: FilterTruthyValuesOptions = {}
): FilterTruthyValues<T> => {
  return Object.fromEntries<FilterTruthyValues<T>>(
    Object.entries(obj).filter(([key, value]) => {
      if (ignoreZero && value === 0) {
        return true;
      }

      if (ignoreFalse && value === false) {
        return true;
      }

      return Boolean(value);
    })
  ) as FilterTruthyValues<T>;
};

export const encodeFilterValueToBase64 = (value?: string | number | boolean | null): string => {
  if (value == null || value === "") {
    return "";
  }

  return window.btoa(encodeURIComponent(value));
};

export const decodeFilterValueFromBase64 = (value?: string | number | boolean | null): string => {
  if (value == null || value === "") {
    return "";
  }

  return decodeURIComponent(window.atob(String(value)));
};

export const serialiseFilters = <T extends Record<string, string>>(
  obj: T
): Record<keyof T, string> => {
  const serialisedFilters = {} as Record<keyof T, string>;

  for (const key in obj) {
    serialisedFilters[key] = encodeURIComponent(obj[key]);
  }

  return serialisedFilters;
};

export const serialiseFiltersToBase64 = <T extends Record<string, string>>(
  obj: T
): Record<keyof T, string> => {
  const serialisedFilters = {} as Record<keyof T, string>;

  for (const key in obj) {
    serialisedFilters[key] = encodeFilterValueToBase64(obj[key]);
  }

  return serialisedFilters;
};

export const deserialiseFilters = <T extends Record<string, string>>(
  urlSearchParams: URLSearchParams
): Record<keyof T, string> => {
  const deserialisedFilters = {} as Record<keyof T, string>;

  urlSearchParams.forEach((value, key) => {
    if (value) {
      deserialisedFilters[key as keyof T] = decodeURIComponent(value);
    } else {
      deserialisedFilters[key as keyof T] = value;
    }
  });

  return deserialisedFilters;
};

export const deserialiseFiltersFromBase64 = <T extends Record<string, string>>(
  urlSearchParams: URLSearchParams
): Record<keyof T, string> => {
  const deserialisedFilters = {} as Record<keyof T, string>;

  urlSearchParams.forEach((value, key) => {
    if (value) {
      deserialisedFilters[key as keyof T] = decodeFilterValueFromBase64(value);
    } else {
      deserialisedFilters[key as keyof T] = value;
    }
  });

  return deserialisedFilters;
};

export const sanitizeString = (value: any): string => {
  return value ? value.toString() : "";
};

export const formatNumberToIndianValue = (
  number?: number | null,
  options?: Intl.NumberFormatOptions
) => {
  if (typeof number !== "number") {
    return "-";
  }

  return number.toLocaleString("en-IN", options);
};

export const formatINR = (number?: number | null, options?: Intl.NumberFormatOptions) => {
  if (typeof number !== "number") {
    return "-";
  }

  const { maximumFractionDigits = 0 } = options || {};

  return `₹ ${formatNumber(number, maximumFractionDigits)}`;
};

export const containsExactlyAllElements = (
  array: any[] | null | undefined,
  arrayToCheck: any[] | null | undefined
) => {
  if (!array || !arrayToCheck) {
    return false;
  }

  if (array?.length !== arrayToCheck?.length) {
    return false;
  }

  return arrayToCheck?.every((element) => array?.includes(element));
};

export function chooseVariant(check: boolean, value1: string, value2: string) {
  return check ? value1 : value2;
}

export const maskBankAccountNumber = (bankAccountNumber?: string) => {
  if (!bankAccountNumber || typeof bankAccountNumber !== "string") {
    return undefined;
  }

  const lastFourChars = bankAccountNumber.slice(-4);

  return `****${lastFourChars}`;
};

export const getErrorDescription = (
  e: any,
  defaultErrorMessage = "Something went wrong",
  customUpstreamErrorPathMessage?: string
) => {
  if (isUpstreamError(e)) {
    if (customUpstreamErrorPathMessage) {
      return customUpstreamErrorPathMessage ?? defaultErrorMessage;
    }
    return e?.data?.error?.message ?? defaultErrorMessage;
  }

  return e.message || defaultErrorMessage;
};

/**
 * Like {@link getErrorDescription}, but for fpapi calls made with `responseType: "blob"`.
 * Axios keeps error bodies as Blobs in that mode, so `e.data.error.message` is not readable.
 *
 * Parses JSON from the blob (or uses an already-parsed object) and reads, in order:
 * - `error.message` — mfpro {@code ApiErrorResponse} wrapper
 * - `detail` — Spring {@code ProblemDetail} / {@code ResponseStatusException}
 * - top-level `message`
 */
export const getErrorDescriptionFromBlobResponse = async (
  e: unknown,
  defaultErrorMessage = "Something went wrong"
): Promise<string> => {
  if (!isUpstreamError(e)) {
    return (e as { message?: string })?.message || defaultErrorMessage;
  }

  const data = (e as { data?: unknown }).data;
  let parsed: Record<string, unknown> | null = null;

  try {
    if (data instanceof Blob) {
      const text = await data.text();
      if (!text?.trim()) {
        return defaultErrorMessage;
      }
      parsed = JSON.parse(text) as Record<string, unknown>;
    } else if (data && typeof data === "object") {
      parsed = data as Record<string, unknown>;
    }
  } catch {
    return defaultErrorMessage;
  }

  if (!parsed) {
    return defaultErrorMessage;
  }

  const nestedError = parsed.error as { message?: string } | undefined;

  return (
    nestedError?.message ??
    (parsed.detail as string | undefined) ??
    (parsed.message as string | undefined) ??
    defaultErrorMessage
  );
};

export const filterSearchParamsByKey = (
  rawParams: URLSearchParams,
  filterKey: string,
  deserialise = false
) => {
  const filteredParams = Array.from(rawParams.entries())
    .filter((param) => param[0].endsWith(filterKey))
    .map((param) => {
      if (param[1] && deserialise) {
        return [
          param[0].slice(0, param[0].length - filterKey.length),
          decodeURIComponent(param[1]),
        ];
      }
      return [param[0].slice(0, param[0].length - filterKey.length), param[1]];
    });
  return Object.fromEntries(filteredParams);
};

export const setSearchParamsByKey = (
  updatedParams: Record<string, string>,
  filterKey: string,
  setSearchParams: SetURLSearchParams
) => {
  setSearchParams(
    Object.fromEntries(
      Object.entries(updatedParams).map((param) => [`${param[0]}${filterKey}`, param[1]])
    )
  );
};

export const downloadFile = (file: Blob, fileName: string) => {
  const link = document.createElement("a");

  const downloadURL = URL.createObjectURL(file);
  link.href = downloadURL;

  link.download = fileName;

  link.click();

  URL.revokeObjectURL(downloadURL);
};

export const hasAllGivenPropertiesInObject = (
  properties: string[],
  obj: Record<string, any> | null | undefined
) => {
  return properties.every((property) => obj?.hasOwnProperty(property));
};

/**
 * Formats a number to a specified number of decimal places using string operations
 * @param value - The number to format (integer or decimal)
 * @param decimalPlaces - Number of decimal places to show (default: 2)
 * @returns Formatted number as string in Indian number format, or original number if it's an integer
 */
export const formatNumber = (value: number, decimalPlaces: number = 0): string => {
  // Handle edge cases
  if (value === 0) {
    return decimalPlaces > 0 ? `0.${"0".repeat(decimalPlaces)}` : "0";
  }

  if (!isFinite(value)) {
    return value.toString();
  }

  // Handle negative numbers
  const isNegative = value < 0;
  const absValue = Math.abs(value);

  // Convert to string to check if it's an integer
  const valueStr = absValue.toString();

  // If decimalPlaces is negative, return the number as it is
  if (decimalPlaces < 0) {
    return isNegative ? `-${valueStr}` : valueStr;
  }

  // If decimalPlaces is 0, return the integer part only
  if (decimalPlaces === 0) {
    const parts = valueStr.split(".");
    const formattedInteger = formatIndianNumber(parts[0]);
    return isNegative ? `-${formattedInteger}` : formattedInteger;
  }

  // Check if the number is an integer (no decimal point)
  if (!valueStr.includes(".")) {
    const formattedInteger = formatIndianNumber(valueStr);
    // Don't pad zeros for integers when decimalPlaces is more than 0
    return isNegative ? `-${formattedInteger}` : formattedInteger;
  }

  // For decimal numbers, truncate to specified decimal places
  const parts = valueStr.split(".");
  const integerPart = parts[0];
  const decimalPart = parts[1];

  // Only truncate if decimal part is longer than requested places
  // Don't pad zeros if decimal part is shorter than requested places
  let finalDecimalPart = decimalPart;
  if (decimalPart.length > decimalPlaces) {
    finalDecimalPart = decimalPart.substring(0, decimalPlaces);
  }

  const formattedInteger = formatIndianNumber(integerPart);
  const result = `${formattedInteger}.${finalDecimalPart}`;

  return isNegative ? `-${result}` : result;
};

export const formatChartExtraData = (
  data: Array<string | number>,
  decimalPlaces: number
): string[] =>
  data.map((item) =>
    !Number.isNaN(Number(item)) ? formatNumber(Number(item), decimalPlaces) : "-"
  );

/**
 * Splits chart tooltip extraData on commas that precede a capitalized label
 * (e.g. "Purchase: 10,00,000, SIP instalments: 5,00,000"), so Indian-grouped
 * numbers are not broken apart.
 */
export const splitTooltipExtraDataByLabel = (extraData: string): string[] =>
  extraData.split(/, (?=[A-Z])/).map((part) => part.trim());

/**
 * Helper function to format numbers in Indian numbering system (1,23,456 format)
 * @param numStr - The number string to format
 * @returns Formatted number string
 */
const formatIndianNumber = (numStr: string): string => {
  // Handle edge cases
  if (numStr === "0") return "0";
  if (numStr.length <= 3) return numStr;

  // Indian numbering system: last 3 digits grouped together, then every 2 digits
  const lastThree = numStr.slice(-3);
  const remaining = numStr.slice(0, -3);

  if (remaining.length === 0) {
    return lastThree;
  }

  // Add commas every 2 digits from right to left for the remaining part
  const formattedRemaining = remaining.replace(/\B(?=(\d{2})+(?!\d))/g, ",");

  return `${formattedRemaining},${lastThree}`;
};

/**
 * Formats a boolean value for display
 * @param value - The boolean value to format
 * @returns "True" if value is true, "False" if value is false, "-" if anything else
 */
export const formatBooleanValue = (value: boolean | null | undefined): string => {
  if (typeof value !== "boolean") {
    return "-";
  }
  return value ? "True" : "False";
};

export const areAllNonExcludedValuesFalsy = <T extends Record<string, any>>(
  objectToCheck: T | null | undefined,
  propertiesToExclude: (keyof T)[] = []
): boolean => {
  if (!objectToCheck) {
    return true;
  }

  const exclusionSet = new Set(propertiesToExclude);

  for (const key in objectToCheck) {
    // If a property is not present in exclusion list and it is a truthy value then return false
    if (!exclusionSet.has(key) && objectToCheck[key]) {
      return false;
    }
  }

  return true;
};

export const getErrorDescriptionWithValidationErrors = (
  e: any,
  defaultErrorMessage = "Something went wrong"
) => {
  if (isUpstreamError(e)) {
    if (e?.status === 400 && Array.isArray(e?.data?.error?.errors)) {
      const validationErrorMessage = e?.data?.error?.errors
        ?.map((error: any) => `${error?.field}: ${error?.message}`)
        .join(", ");

      return validationErrorMessage ?? defaultErrorMessage;
    }

    return e?.data?.error?.message ?? defaultErrorMessage;
  }

  return e.message ?? defaultErrorMessage;
};

/**
 * Formats a number according to Indian terminology
 * @param amount - The number to format (can be string, number, or null)
 * @returns Empty string if null, otherwise formatted number in Crores, <1K if less than 1000
 */
export const formatAmountInCrore = (amount: string | number | null): string => {
  if (amount === null || amount === undefined) {
    return "";
  }

  // Convert string to number if needed
  let numericAmount: number;
  if (typeof amount === "string") {
    numericAmount = parseFloat(amount);
  } else {
    numericAmount = amount;
  }

  if (typeof numericAmount !== "number" || !isFinite(numericAmount)) {
    return "";
  }

  const absAmount = Math.abs(numericAmount);
  const isNegative = numericAmount < 0;

  // Indian numbering system threshold - only format if >= 1 Crore
  const CRORE = 10000000; // 1 Crore = 10 Million

  let formattedAmount: string;
  const crores = absAmount / CRORE;
  if (absAmount === 0) {
    formattedAmount = formatINR(crores, { maximumFractionDigits: 2 });
  } else if (absAmount < 1000) {
    formattedAmount = `<1K`;
  } else if (absAmount < CRORE) {
    formattedAmount = `${formatINR(crores, { maximumFractionDigits: 4 })} Cr`;
  } else {
    formattedAmount = `${formatINR(crores, { maximumFractionDigits: 2 })} Cr`;
  }

  return isNegative ? `-${formattedAmount}` : formattedAmount;
};

export const longMonthNameToIndexMap: Record<string, number> = {
  January: 0,
  February: 1,
  March: 2,
  April: 3,
  May: 4,
  June: 5,
  July: 6,
  August: 7,
  September: 8,
  October: 9,
  November: 10,
  December: 11,
};

export const shortMonthNameToIndexMap: Record<string, number> = {
  Jan: 0,
  Feb: 1,
  Mar: 2,
  Apr: 3,
  May: 4,
  Jun: 5,
  Jul: 6,
  Aug: 7,
  Sep: 8,
  Oct: 9,
  Nov: 10,
  Dec: 11,
};

export const removeAdditionalWhitespaceAndTrimString = (str: string) => {
  return str.replaceAll(/\s+/g, " ").trim();
};

/**
 * Converts a long month year date string to a date object
 * @param longMonthYearDateStr - The long month year date string to convert to date object. Ex. "January 2026"
 * @returns The date object. Ex. new Date(2026, 0)
 */
export const convertLongMonthYearDateStringtoDateObject = (longMonthYearDateStr: string) => {
  const [monthStr, year] = removeAdditionalWhitespaceAndTrimString(longMonthYearDateStr).split(" ");

  return new Date(+year, longMonthNameToIndexMap[monthStr]);
};

export const truncateDecimalString = (
  decimalString: string | undefined | null,
  decimalPlaces: number = 4
) => {
  if (!decimalString) {
    return "-";
  }

  const [integerPart, decimalPart] = decimalString.split(".");

  if (decimalPart) {
    return `${integerPart}.${decimalPart.slice(0, decimalPlaces)}`;
  }

  return decimalString;
};

export const getBooleanOrNull = (value: boolean | null | undefined): boolean | null => {
  if (value === null || value === undefined) {
    return null;
  }

  return Boolean(value);
};

export const getFilterLabelsFromFilterPillValues = (
  filterValues: string | null | undefined,
  filterOptions: { id?: string; label: string; value: string }[]
) => {
  return filterValues
    ?.split(",")
    ?.map((filterValueSegment) => {
      return filterOptions[getOptionsIndex(filterOptions, filterValueSegment)]?.label;
    })
    .join(", ");
};

/**
 * Generates an RFC 4122 v4 UUID using the Web Crypto API.
 * Uses `crypto.randomUUID()` when available (secure contexts); falls back to a
 * `crypto.getRandomValues()`-based v4 generator otherwise (e.g. plain-http envs
 * where `randomUUID` is undefined).
 *
 * @example
 * generateUUID() // "9b1deb4d-3b7d-4bad-9bdd-2b0d7b3dcb6d"
 * generateUUID().replace(/-/g, "") // "9b1deb4d3b7d4bad9bdd2b0d7b3dcb6d" (32 hex chars)
 */
export const generateUUID = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

/**
 * Returns a cryptographically secure random number in the range [min, max).
 *
 * @param min - Inclusive lower bound. Defaults to 0.
 * @param max - Exclusive upper bound. Defaults to 1.
 */
export const getRandomNumber = (min = 0, max = 1): number => {
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    throw new TypeError("min and max must be finite numbers");
  }

  if (min >= max) {
    throw new RangeError("min must be less than max");
  }

  const randomValues = new Uint32Array(1);

  // crypto.getRandomValues() provides cryptographically secure random bytes.
  crypto.getRandomValues(randomValues);

  // Convert the 32-bit unsigned integer to a cryptographically secure
  // floating-point value in the range [0, 1).
  const random = randomValues[0] / 2 ** 32;

  return min + random * (max - min);
};

/**
 * Returns a cryptographically secure random integer in the range [min, max).
 *
 * Rejection sampling is used to avoid modulo bias, ensuring every possible
 * integer in the range has the same probability of being selected.
 *
 * @param min - Inclusive lower bound.
 * @param max - Exclusive upper bound.
 */
export const getRandomInteger = (min: number, max: number): number => {
  if (!Number.isSafeInteger(min) || !Number.isSafeInteger(max)) {
    throw new TypeError("min and max must be safe integers");
  }

  if (min >= max) {
    throw new RangeError("min must be less than max");
  }

  const range = max - min;

  // Uint32Array produces values in the range [0, 2^32).
  // The range must therefore fit within the available 32-bit values.
  if (range > 2 ** 32) {
    throw new RangeError("The range must not exceed 2^32");
  }

  const randomValues = new Uint32Array(1);

  // Discard the uneven portion of the random space so that modulo
  // does not introduce bias when the range does not divide 2^32 evenly.
  const limit = Math.floor(2 ** 32 / range) * range;

  do {
    crypto.getRandomValues(randomValues);
  } while (randomValues[0] >= limit);

  return min + (randomValues[0] % range);
};

/**
 * Returns a cryptographically secure random element from an array.
 *
 * @param arr - Array of strings to choose from.
 * @returns A randomly selected element, or an empty string if the array is empty.
 */
export const getRandomElementFromArray = (arr: string[]): string => {
  if (!arr.length) {
    return "";
  }

  // max is exclusive, so this produces an index in [0, arr.length).
  return arr[getRandomInteger(0, arr.length)];
};
